import type {
  UserActionsConfig,
  UserActionsState,
  QueryParamsResult,
  BotDetectionChecks,
  MetricsPayload,
  PWUserActionsAPI, PWTrackingEvent,
} from './types';
import {
  deepMerge,
  getCookie,
  setCookie,
  getStashedUserEmail,
  isConsentGranted,
  isTrackingOn,
  DEFAULTS, setStashedUserEmail,
} from './utils';

(function pwUserActions(): void {
  'use strict';

  // =============================================================================
  // 1. CONFIGURATION
  // =============================================================================

  const preConfig = window.PW_USER_ACTIONS_CONFIG || {};
  const trackingEvents = Array.isArray(window.pwTrackingEventsStore) ? [...window.pwTrackingEventsStore] : [] ;

  // =============================================================================
  // 2. UTILITY FUNCTIONS
  // =============================================================================

  function generateUserId(): string {
    let generatedUserId = '';
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      generatedUserId = crypto.randomUUID();
    } else {
      generatedUserId = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = Math.floor(Math.random() * 16);
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
      });
    }
    setCookie(config.cookie.key, generatedUserId, config.cookie);
    return generatedUserId;
  }

  function getQueryParams(queryString?: string): QueryParamsResult {
    const qs = queryString || window.location.search;
    if (!qs) return { queryParams: {}, utm: {} };

    return qs.slice(1).split('&').reduce<QueryParamsResult>(
      (acc, pair) => {
        if (!pair) return acc;
        const keyValue = pair.split('=').map(decodeURIComponent);
        const key = keyValue[0];
        const value = keyValue[1];

        if (!key) return acc;

        if (key.indexOf('utm_') === 0) {
          acc.utm[key] = value;
        } else {
          acc.queryParams[key] = value;
        }
        return acc;
      },
      { queryParams: {}, utm: {} }
    );
  }

  function getPage(): string {
    return window.location.protocol + '//' + window.location.hostname + window.location.pathname;
  }

  function extractAppId(url: string): string | null {
    const pattern = /\/(applications|journeys)\/([^/]+)/;
    const match = url.match(pattern);
    return match ? match[2] : null;
  }

  function safeText(el: Element | null): string {
    try {
      return (el && el.textContent ? el.textContent : '').trim();
    } catch {
      return '';
    }
  }

  function validateEmail(email: string): boolean {
    return !!String(email)
      .toLowerCase()
      .match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      );
  }

  // =============================================================================
  // 3. BOT DETECTION
  // =============================================================================

  function buildChecks(): BotDetectionChecks {
    return {
      webdriver: navigator.webdriver,
      headlessChrome: /HeadlessChrome/.test(navigator.userAgent),
      chromeInconsistency: /Chrome/.test(navigator.userAgent) && !window.chrome,
      pluginCount: navigator.plugins && navigator.plugins.length ? navigator.plugins.length : 0,
      languageCount:
        navigator.languages && navigator.languages.length ? navigator.languages.length : 0,
      notificationPermission: (function () {
        try {
          return window.Notification ? Notification.permission : 'unsupported';
        } catch {
          return 'error';
        }
      })(),
      webglRenderer: (function () {
        try {
          const canvas = document.createElement('canvas');
          const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
          const debugInfo = gl ? (gl as WebGLRenderingContext).getExtension('WEBGL_debug_renderer_info') : null;
          const renderer =
            gl && debugInfo ? (gl as WebGLRenderingContext).getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) : null;
          return renderer || 'unknown';
        } catch {
          return 'error';
        }
      })(),
      hasWebGL: !!window.WebGLRenderingContext,
      hasWebRTC: !!window.RTCPeerConnection,
      hasAudioContext: !!(window.AudioContext || window.webkitAudioContext),
      loadTime:
        window.performance && performance.timing
          ? performance.timing.domContentLoadedEventEnd - performance.timing.navigationStart
          : null,
      screenMatch:
        typeof screen !== 'undefined'
          ? screen.width >= window.outerWidth && screen.height >= window.outerHeight
          : null,
      phantom: !!(window.callPhantom || window._phantom),
      nightmare: !!window.__nightmare,
      puppeteer: !!(
        navigator.webdriver ||
        window.__puppeteer_evaluation_script__ ||
        /puppeteer/i.test(navigator.userAgent)
      ),
      ua: navigator.userAgent,
    };
  }

  // =============================================================================
  // 4. METRICS SENDING
  // =============================================================================

  function sendMetrics(data: MetricsPayload): void {
    if (!isTrackingOn(config.isProduction, config.localStorageOptOutKey)) {
      return;
    }

    const page = getPage();
    const extendedAttrs: Record<string, unknown> = Object.assign({}, data.attributes, {
      ln: state.ln,
    });

    if (isConsentGranted()) {
      if (state.userEmail) extendedAttrs.userEmail = state.userEmail;
      if (state.accId) extendedAttrs.acc_id = state.accId;
    }

    const attributesWithChecks: Record<string, unknown> = Object.assign({}, extendedAttrs, {
      checks: buildChecks(),
    });
    const appId = extractAppId(window.location.href);
    if (appId) attributesWithChecks.app_id = appId;

    const metabasePayload = Object.assign({}, data, {
      page,
      user_id: state.userId,
      attributes: JSON.stringify(attributesWithChecks),
    });

    try {
      if (navigator.sendBeacon && config.beaconUrl) {
        navigator.sendBeacon(config.beaconUrl, JSON.stringify(metabasePayload));
      } else if (config.beaconUrl) {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', config.beaconUrl, true);
        xhr.setRequestHeader('Content-Type', 'text/plain;charset=UTF-8');
        xhr.send(JSON.stringify(metabasePayload));
      }
    } catch (error) {
      if (!config.isProduction) {
        console.error('Error sending user tracking metrics to Pushwoosh server', error);
      }
    }
  }

  // =============================================================================
  // 5. EVENT HANDLERS
  // =============================================================================

  function setupClickHandler(): void {
    window.addEventListener(
      'click',
      (event: MouseEvent) => {
        const target = event.target as Element | null;
        if (!target || !target.closest) return;

        const link = target.closest('a');
        const button = target.closest('button');
        const youtubeVideoTag = target.closest('lite-youtube');

        if (youtubeVideoTag) {
          const videoId = youtubeVideoTag.getAttribute('videoid');
          const playlabel = youtubeVideoTag.getAttribute('playlabel');
          const href = videoId ? 'https://www.youtube.com/watch?v=' + videoId : null;

          const ytAttrs: Record<string, unknown> = { title: playlabel };
          if (href) ytAttrs.href = href;

          sendMetrics({
            action_type: 'YOUTUBE_CLICK',
            attributes: ytAttrs,
          });
        }

        if (link || button) {
          const el = link || button;
          const clickAttrs: Record<string, unknown> = { clickedText: safeText(target) };
          if (link && link.href) clickAttrs.href = link.href;

          const track = el!.getAttribute('data-track');
          if (track) clickAttrs.track = track;

          sendMetrics({
            action_type: 'CLICK',
            attributes: clickAttrs,
          });
        }
      },
      true
    );
  }

  function setupScrollHandler(): void {
    window.addEventListener(
      'scroll',
      () => {
        const doc = document.documentElement;
        const denom = doc.scrollHeight - window.innerHeight;
        if (!denom) return;

        let scrolledPercentage = (window.scrollY / denom) * 100;
        scrolledPercentage = scrolledPercentage > 99 ? 100 : scrolledPercentage;

        const thresholdKeys = Object.keys(state.thresholds);

        for (let i = 0; i < thresholdKeys.length; i++) {
          const key = thresholdKeys[i];
          const threshold = Number(key);
          const reached = state.thresholds[threshold];

          if (scrolledPercentage >= threshold && !reached) {
            sendMetrics({
              action_type: 'SCROLL',
              attributes: { threshold: threshold },
            });
            state.thresholds[threshold] = true;
          }
        }
      },
      { passive: true }
    );
  }

  // =============================================================================
  // 6. INITIALIZE CONFIG & STATE
  // =============================================================================

  let config: UserActionsConfig = deepMerge(DEFAULTS as UserActionsConfig, preConfig);

  const state: UserActionsState = {
    ln: navigator.language,
    userId: null,
    userEmail: null,
    accId: null,
    isFirstTimeVisit: false,
    thresholds: { 25: false, 50: false, 75: false, 100: false },
    attributes: {
      ref: document.referrer,
    },
  };

  // Initialize user ID
  const existingUserIdCookie = getCookie(config.cookie.key);
  state.userId = existingUserIdCookie || generateUserId();
  state.isFirstTimeVisit = !existingUserIdCookie;

  if (state.isFirstTimeVisit) {
    state.attributes.n_user = true;
  }

  window.pwTrackingEventsStore = {
    push: function (event: PWTrackingEvent) {
      if (!isTrackingOn(config.isProduction, config.localStorageOptOutKey)) {
        return;
      }

      console.debug(event);
    }
  }

  // =============================================================================
  // 7. PUBLIC API
  // =============================================================================

  window.PWUserActions = window.PWUserActions || ({} as PWUserActionsAPI);
  const api = window.PWUserActions;

  api.init = function init(overrides) {
    config = deepMerge(config, overrides || {});

    const stashedUserEmail = getStashedUserEmail(config.isProduction);
    if (stashedUserEmail && validateEmail(stashedUserEmail)) {
      state.userEmail = stashedUserEmail;
    }

    if (overrides?.user) {
      api.identifyUser(overrides.user, overrides.account);
    }
    // Track initial page visit
    api.onRouteChange();

    trackingEvents.forEach((event) => {
      window.pwTrackingEventsStore && window.pwTrackingEventsStore.push(event);
    });

    return api;
  };

  api.getUserId = function getUserId() {
    return state.userId;
  };

  api.identifyUser = function identifyUser(user, account) {
    state.userEmail = user.email;
    state.accId = account?.id ?? null;

    const stashedUserEmail = getStashedUserEmail(config.isProduction);
    const wasPushwooshUser = stashedUserEmail && stashedUserEmail.endsWith('@pushwoosh.com');
    const isCurrentPushwooshUser = user.email.endsWith('@pushwoosh.com');

    // Always identify real Pushwoosh users.
    // When logged in via super admin, user.email may belong to a client.
    // To avoid overwriting identity while debugging client profiles,
    // we keep the previous Pushwoosh identity if it was already set.
    if (wasPushwooshUser && !isCurrentPushwooshUser) {
      return;
    }

    setStashedUserEmail(state.userEmail, config.cookie, config.isProduction);
  };

  api.onRouteChange = function onRouteChange() {
    // Reset scroll thresholds for new route
    const thresholdKeys = Object.keys(state.thresholds);
    for (let i = 0; i < thresholdKeys.length; i++) {
      state.thresholds[Number(thresholdKeys[i])] = false;
    }

    // Update UTM and query params
    const queryParamsObj = getQueryParams();
    if (Object.keys(queryParamsObj.utm).length) {
      state.attributes.utm = queryParamsObj.utm;
    }
    if (Object.keys(queryParamsObj.queryParams).length) {
      state.attributes.queryParams = queryParamsObj.queryParams;
    }

    sendMetrics({
      action_type: 'PAGE_VISIT',
      attributes: Object.assign({}, state.attributes),
    });
  };

  // =============================================================================
  // 8. FLOW
  // =============================================================================

  // Setup event listeners
  setupClickHandler();
  setupScrollHandler();
})();
